import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { ActivityEvent, ActivityStats, BuilderActivity } from '../../services/systemApi';

const WINDOWS = [1, 7, 30];

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 1000) / 10}%`);
const secs = (ms: number | null) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const when = (iso: string | null) => (iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString() : '—');

const successColor = (s: ActivityStats): 'success' | 'warning' | 'error' | 'default' => {
  if (!s.calls || s.success_rate == null) return 'default';
  if (s.success_rate >= 0.97) return 'success';
  return s.success_rate >= 0.85 ? 'warning' : 'error';
};

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Card variant="outlined" sx={{ mb: 2.5 }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const EventsTable: React.FC<{ rows: ActivityEvent[]; labels: Record<string, string>; modelName: (id: string | null) => string; empty: string }> = ({
  rows,
  labels,
  modelName,
  empty,
}) =>
  rows.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      {empty}
    </Typography>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>When</TableCell>
          <TableCell>Builder</TableCell>
          <TableCell>Model</TableCell>
          <TableCell>Error</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={`${r.at}-${i}`}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(r.at)}</TableCell>
            <TableCell>{labels[r.feature] || r.feature}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {modelName(r.model)}
              {r.attempts && r.attempts > 1 ? ` (${r.attempts} tried)` : ''}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-word', maxWidth: 480 }}>
              {r.error || '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

interface BuilderActivityPanelProps {
  load: (days: number) => Promise<BuilderActivity>;
  modelName: (id: string | null) => string;
}

/** System Settings Activity tab: are the builder AIs running, on the models we picked, and how well. */
export const BuilderActivityPanel: React.FC<BuilderActivityPanelProps> = ({ load, modelName }) => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<BuilderActivity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    load(days)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [load, days]);

  useEffect(refresh, [refresh]);

  if (!data) {
    return error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={24} />;
  }

  const labels: Record<string, string> = Object.fromEntries([
    ...data.features.map((f) => [f.feature, f.label]),
    ...data.public_functions.map((f) => [f.function, f.label]),
  ]);
  const maxDaily = Math.max(1, ...data.daily.map((d) => d.calls));
  const totalCalls = data.features.reduce((n, f) => n + f.calls, 0);

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          {WINDOWS.map((d) => (
            <ToggleButton key={d} value={d}>
              {d === 1 ? 'Today' : `${d} days`}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={refresh} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {data.settings_updated_at
            ? `Settings last changed ${when(data.settings_updated_at)} by ${data.settings_updated_by || 'unknown'}`
            : 'Settings have never been changed (built-in defaults)'}
        </Typography>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Alert severity="info" variant="outlined" sx={{ mb: 2.5 }}>
        Every call to a builder AI is recorded here (no prompts or replies, only the model, outcome and time). After changing a
        model, check <b>Since last change</b>: once people use that builder it should show their calls answered by the new
        primary. <b>Backup used</b> means the primary failed or was busy and a fallback answered. Times are for successful calls;
        "typical" is the median and "slow" is the 95th percentile.
      </Alert>

      <Section title="Builder AIs" subtitle="The builders whose models you set on the Settings tab.">
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Builder</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell align="right">Backup used</TableCell>
                <TableCell align="right">Typical / slow</TableCell>
                <TableCell>Primary model</TableCell>
                <TableCell>Since last change</TableCell>
                <TableCell>Last call</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.features.map((f) => {
                const sc = f.since_change;
                const offPrimary = sc && sc.calls > 0 && sc.on_primary / sc.calls < 0.8;
                return (
                  <TableRow key={f.feature}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {f.label}
                      </Typography>
                      {!f.enabled && <Chip size="small" label="Switched off" color="warning" sx={{ mt: 0.5 }} />}
                    </TableCell>
                    <TableCell align="right">{f.calls.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {f.calls ? <Chip size="small" label={pct(f.success_rate)} color={successColor(f)} variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell align="right">{f.ok ? `${f.fallback} (${pct(f.fallback / f.ok)})` : '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {secs(f.p50_ms)} / {secs(f.p95_ms)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{modelName(f.primary)}</TableCell>
                    <TableCell>
                      {!sc ? (
                        '—'
                      ) : sc.calls === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No calls yet
                        </Typography>
                      ) : (
                        <Tooltip title={f.models.map((m) => `${modelName(m.model)}: ${m.calls}`).join(' · ')}>
                          <Chip
                            size="small"
                            color={offPrimary ? 'warning' : 'success'}
                            variant="outlined"
                            label={`${sc.on_primary} of ${sc.calls} on primary`}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {f.last_at ? when(f.last_at) : '—'}
                      {f.last_model && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {modelName(f.last_model)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {totalCalls > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="caption" color="text.secondary">
              Calls per day (red = failed)
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ height: 64, mt: 0.5 }}>
              {data.daily.map((d) => (
                <Tooltip key={d.date} title={`${d.date}: ${d.calls} calls, ${d.failed} failed`}>
                  <Box sx={{ flex: 1, minWidth: 4, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                    <Box sx={{ height: `${(d.failed / maxDaily) * 100}%`, bgcolor: 'error.light' }} />
                    <Box sx={{ height: `${((d.calls - d.failed) / maxDaily) * 100}%`, bgcolor: 'primary.light', borderRadius: '2px 2px 0 0' }} />
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        )}
        {data.truncated && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Only the most recent 20,000 calls are included. Pick a shorter window for exact numbers.
          </Alert>
        )}
      </Section>

      <Section title="Recent failures" subtitle="Calls where every model in the list failed. The person saw an error.">
        <EventsTable rows={data.recent_failures} labels={labels} modelName={modelName} empty="No failures in this window." />
      </Section>

      <Section title="Recent backup answers" subtitle="Calls a fallback model answered, with the error the earlier model gave.">
        <EventsTable rows={data.recent_fallbacks} labels={labels} modelName={modelName} empty="Every call was answered by its primary model." />
      </Section>

      <Section
        title="Function Builder helpers"
        subtitle="Public AI functions behind Describe it and Change it with AI. Their models are set on each function (Function Builder, public mode), not on the Settings tab."
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Function</TableCell>
              <TableCell align="right">Runs</TableCell>
              <TableCell align="right">Success</TableCell>
              <TableCell align="right">Typical / slow</TableCell>
              <TableCell>Models used</TableCell>
              <TableCell>Last run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.public_functions.map((f) => (
              <TableRow key={f.function}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {f.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {f.function}
                  </Typography>
                </TableCell>
                <TableCell align="right">{f.calls.toLocaleString()}</TableCell>
                <TableCell align="right">
                  {f.calls ? <Chip size="small" label={pct(f.success_rate)} color={successColor(f)} variant="outlined" /> : '—'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {secs(f.p50_ms)} / {secs(f.p95_ms)}
                </TableCell>
                <TableCell>{f.models.length ? f.models.map((m) => `${modelName(m.model)} (${m.calls})`).join(', ') : '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(f.last_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.public_failures.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Recent failed runs
            </Typography>
            <EventsTable rows={data.public_failures} labels={labels} modelName={modelName} empty="" />
          </Box>
        )}
      </Section>
    </Box>
  );
};

export default BuilderActivityPanel;
